let logoutFromAuthPromise: Promise<unknown> | null = null;

const AUTH_COOKIE_NAMES = [
  'next-auth.session-token',
  '__Secure-next-auth.session-token',
  'next-auth.csrf-token',
  '__Host-next-auth.csrf-token',
  'next-auth.callback-url',
  '__Secure-next-auth.callback-url',
  'authjs.session-token',
  '__Secure-authjs.session-token',
  'authjs.csrf-token',
  '__Host-authjs.csrf-token',
  'authjs.callback-url',
  '__Secure-authjs.callback-url',
];

export const useLogoutFromAuth = async () => {
  if (logoutFromAuthPromise) {
    return logoutFromAuthPromise;
  }

  logoutFromAuthPromise = logoutFromAuth();

  return logoutFromAuthPromise.finally(() => {
    logoutFromAuthPromise = null;
  });
};

const logoutFromAuth = async () => {
  const { signOut } = useAuth();
  const localePath = useLocalePath();

  // `useI18n()` can't be called here because this function is executed from a
  // click handler (outside the component setup call stack), which triggers:
  // "Must be called at the top of a `setup` function".
  // Instead, read the locale from the injected Nuxt i18n instance.
  const nuxtApp = useNuxtApp();
  const composerLocale: unknown = (nuxtApp as any)?.$i18n?.locale;
  const rawLocale =
    composerLocale &&
    typeof composerLocale === 'object' &&
    'value' in composerLocale
      ? (composerLocale as any).value
      : composerLocale;
  const stableLocale: 'en' | 'ar' = rawLocale === 'ar' ? 'ar' : 'en';

  // Keep the current i18n locale on logout.
  // When signOut redirects automatically it may navigate to an un-localized
  // path (and sometimes triggers a full reload), which makes the login page
  // fall back to the default language.
  if (import.meta.client) {
    clearClientSessionState(stableLocale);
  }

  await signOut({ redirect: false });

  if (import.meta.client) {
    expireAuthCookies();
    persistLocale(stableLocale);
  }

  return navigateTo(localePath('/auth/login', stableLocale), { replace: true });
};

export const useLogoutFromServer = async () => {
  // Always end the client session, even if the backend logout call fails.
  //
  // The backend POST /auth/logout used to be skipped on error — when it
  // 500'd (e.g. the missing `login_histories.token_id` column bug) the
  // client kept the user signed in, leaving them stuck on a "logout
  // failed" toast with no way out. Defensive try/finally is correct here:
  // the *user-visible* contract of "I clicked Logout" is "I am now
  // signed out", regardless of how server-side audit-row hygiene went.
  //
  // Using `$fetch` directly (not `useFetchApi`) for two reasons:
  //   • `useFetchApi` is `lazy: true` — `data.value` is null right after
  //     the await, so the old success-gate `if (data.value)` was racing
  //     with reactivity even on the happy path.
  //   • `useFetchApi` throws on 4xx/5xx via createError, which would have
  //     skipped the client signOut. We want signOut() to run no matter what.
  try {
    await $fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch (err) {
    // Backend audit-row update failed — log for ops, but never block the
    // user's logout. The session cookie is still cleared by signOut().
    console.warn('[useLogoutFromServer] backend logout call failed', err);
  } finally {
    await useLogoutFromAuth();
  }
};

const clearClientSessionState = (stableLocale: 'en' | 'ar') => {
  try {
    sessionStorage.clear();
  } catch {
    // Ignore storage failures in restricted browser modes.
  }

  try {
    localStorage.clear();
  } catch {
    // Ignore storage failures in restricted browser modes.
  }

  try {
    clearNuxtData();
  } catch {
    // Nuxt data cache may be unavailable in some test/runtime contexts.
  }

  const permissions = useState<Record<string, boolean>>(
    'permissions',
    () => ({}),
  );
  const permissionsLoaded = useState<boolean>('permissionsLoaded', () => true);
  permissions.value = {};
  permissionsLoaded.value = true;
  persistLocale(stableLocale);
};

const persistLocale = (stableLocale: 'en' | 'ar') => {
  try {
    localStorage.setItem('app_locale', stableLocale);
  } catch {
    // Ignore storage failures in restricted browser modes.
  }

  try {
    document.cookie = `i18n_redirected=${encodeURIComponent(stableLocale)}; path=/; max-age=31536000`;
  } catch {
    // Ignore cookie failures in restricted browser modes.
  }
};

const expireAuthCookies = () => {
  const paths = new Set(['/', window.location.pathname || '/']);
  const host = window.location.hostname;
  const domains = new Set<string>(['']);

  if (host && host.includes('.')) {
    domains.add(host);
    domains.add(`.${host.replace(/^\./, '')}`);
  }

  for (const name of AUTH_COOKIE_NAMES) {
    for (const path of paths) {
      for (const domain of domains) {
        const domainPart = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; path=${path}${domainPart}; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    }
  }
};
